import type {
  CityProgressDTO,
  InviteDetailDTO,
  LivePulseDTO,
  WorldSnapshotDTO
} from "@light-earth/contracts";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  Download,
  Globe2,
  MapPin,
  Share2,
  Sparkles,
  X
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  fetchCities,
  fetchInvite,
  fetchWorld,
  recordAppStoreClick,
  recordInviteClick,
  subscribeToWorld
} from "./api";
import { WorldMap } from "./components/WorldMap";
import { LegalPage } from "./LegalPage";
import { previewCities, previewWorld } from "./previewData";

export const APP_STORE_URL = "https://apps.apple.com/app/id6796595780";

export function App() {
  const legalKind = legalKindForPath(window.location.pathname);
  if (legalKind) {
    return <LegalPage kind={legalKind} />;
  }

  const [world, setWorld] = useState<WorldSnapshotDTO>(previewWorld);
  const [cities, setCities] = useState<CityProgressDTO[]>(previewCities);
  const [selectedCity, setSelectedCity] = useState<CityProgressDTO>();
  const [connected, setConnected] = useState(false);
  const [latestPulse, setLatestPulse] = useState<LivePulseDTO>();
  const [shareState, setShareState] = useState<"idle" | "copied">("idle");
  const nativeShareAvailable = typeof (
    navigator as Navigator & { share?: Navigator["share"] }
  ).share === "function";
  const useNativeShare = nativeShareAvailable
    && window.matchMedia("(pointer: coarse), (max-width: 767px)").matches;
  const [menuOpen, setMenuOpen] = useState(false);
  const inviteCode = useMemo(
    () => inviteCodeFromSearch(window.location.search),
    []
  );
  const [invite, setInvite] = useState<InviteDetailDTO>();
  const campaignClock = useCampaignClock(world.startsAt, world.endsAt);
  const campaignPhase = campaignClock.phase;
  const worldShareText = campaignPhase === "scheduled"
    ? "The map starts dark. It only lights up when real people walk for five minutes. 72 hours. One planet. Can humanity turn it on? #EarthRelay"
    : campaignPhase === "live"
      ? `${formatCompact(world.totalSteps)} of ${formatCompact(world.stepGoal)} steps are lighting Earth across ${world.regionsReached} countries and regions. ${formatCompact(world.relayPasses)} relays passed. Carry it forward. #EarthRelay`
      : `Humanity finished the first Earth Relay with ${formatCompact(world.totalSteps)} verified steps across ${world.regionsReached} countries and regions, with ${formatCompact(world.relayPasses)} relays passed. #EarthRelay`;
  const worldShareURL = socialShareURL(
    window.location.href,
    campaignPhase,
    world.totalSteps
  );
  const appStoreDestination = appStoreDestinationForPhase(campaignPhase);
  const primaryActionDestination = invite || campaignPhase === "live"
    ? appStoreDestination
    : campaignPhase === "scheduled"
      ? "#download"
      : "#world";

  useEffect(() => {
    void Promise.all([fetchWorld(), fetchCities()])
      .then(([nextWorld, nextCities]) => {
        setWorld(nextWorld);
        setCities(nextCities);
        setLatestPulse(nextWorld.recentPulses[0]);
      })
      .catch(() => {
        // The bundled state stays truthful while the live endpoint reconnects.
      });
    return subscribeToWorld({
      onSnapshot: (snapshot) => {
        setWorld(snapshot);
        setLatestPulse(snapshot.recentPulses[0]);
      },
      onPulse: (pulse) => {
        setLatestPulse(pulse);
        void fetchCities().then(setCities).catch(() => undefined);
        setWorld((current) => ({
          ...current,
          recentPulses: [pulse, ...current.recentPulses].slice(0, 30)
        }));
      },
      onStatus: setConnected
    });
  }, []);

  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    void fetchInvite(inviteCode)
      .then((detail) => {
        if (!cancelled) setInvite(detail);
      })
      .catch(() => undefined);

    try {
      const storageKey = `light-earth-invite-click:${inviteCode}`;
      if (!window.sessionStorage.getItem(storageKey)) {
        window.sessionStorage.setItem(storageKey, "1");
        void recordInviteClick(inviteCode).catch(() => undefined);
      }
    } catch {
      // Aggregate attribution remains optional when session storage is blocked.
    }
    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  const topCities = useMemo(
    () => [...cities].sort((a, b) => b.activeToday - a.activeToday).slice(0, 4),
    [cities]
  );

  async function share(): Promise<void> {
    const shareData = {
      title: "Earth Relay — 72 hours to light Earth",
      text: worldShareText,
      url: worldShareURL
    };
    if (useNativeShare) {
      try {
        await navigator.share(shareData);
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
      setShareState("copied");
      window.setTimeout(() => setShareState("idle"), 1_800);
    } catch {
      // The direct X link remains available whenever native sharing is absent.
    }
  }

  function trackAppStoreOutbound(): void {
    if (campaignPhase !== "live") return;
    const attribution = inviteCode ?? "direct";
    try {
      const storageKey = `earth-relay-app-store-click:${attribution}`;
      if (window.sessionStorage.getItem(storageKey)) return;
      window.sessionStorage.setItem(storageKey, "1");
    } catch {
      // The aggregate conversion signal remains optional in restricted browsers.
    }
    void recordAppStoreClick(inviteCode).catch(() => undefined);
  }

  return (
    <main>
      <header className="site-header">
        <a className="brand" href="#" aria-label="Earth Relay home">
          <span className="brand-mark"><Globe2 size={21} /></span>
          <span>EARTH RELAY</span>
        </a>
        <nav className="desktop-nav" aria-label="Main navigation">
          <a href="#live">World map</a>
          <a href="#how">How it works</a>
          <a href="#privacy">Privacy</a>
        </nav>
        <div className="header-actions">
          <span
            className={`connection ${
              connected && campaignPhase === "live" ? "is-live" : ""
            }`}
          >
            <span className="live-dot" />
            {!connected
              ? "RECONNECTING"
              : campaignPhase === "scheduled"
                ? "READY"
                : campaignPhase === "live"
                  ? "LIVE"
                  : "COMPLETE"}
          </span>
          <a
            className="button button--small"
            href={appStoreDestination}
            onClick={campaignPhase === "live" ? trackAppStoreOutbound : undefined}
          >
            {campaignPhase === "scheduled" ? "LAUNCH DETAILS" : "GET THE IPHONE APP"}
            <ArrowUpRight size={15} />
          </a>
          <button
            className="menu-button"
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            {menuOpen ? <X /> : <span>☰</span>}
          </button>
        </div>
        {menuOpen ? (
          <nav className="mobile-nav">
            <a href="#live" onClick={() => setMenuOpen(false)}>World map</a>
            <a href="#how" onClick={() => setMenuOpen(false)}>How it works</a>
            <a href="#privacy" onClick={() => setMenuOpen(false)}>Privacy</a>
            <a
              className="mobile-nav__store"
              href={appStoreDestination}
              onClick={() => {
                setMenuOpen(false);
                if (campaignPhase === "live") trackAppStoreOutbound();
              }}
            >
              {campaignPhase === "scheduled" ? "Launch details" : "Get the iPhone app"}
              <ArrowUpRight size={15} />
            </a>
          </nav>
        ) : null}
      </header>

      <section className="hero" id="live">
        <div className="ambient ambient--one" />
        <div className="ambient ambient--two" />
        <div className="hero-copy">
          <div className="eyebrow">
            <Sparkles size={14} />
            {invite
              ? `LIGHT SENT FROM ${invite.city.toUpperCase()}`
              : campaignPhase === "scheduled"
                ? "72-HOUR EARTH RELAY · LAUNCHING SOON"
                : campaignPhase === "live"
                  ? "72-HOUR EARTH RELAY · LIVE NOW"
                  : "72-HOUR EARTH RELAY · COMPLETE"}
          </div>
          <h1>
            {invite ? (
              <>
                A light reached you
                <br />
                from <em>{invite.city}.</em>
              </>
            ) : (
              <>
                One planet.
                <br />
                <em>Light it together.</em>
              </>
            )}
          </h1>
          <p className="hero-lead">
            {invite
              ? `Contributor #${invite.contributorNumber.toLocaleString()} lit ${invite.city}. Walk five minutes to add your own city and carry the light forward.`
              : campaignPhase === "scheduled"
                ? "The map starts dark. Every verified five-minute walk adds light. When the iPhone app launches, humanity gets 72 hours to reach 10 million steps together."
                : campaignPhase === "live"
                  ? "No teams and no borders—just one human relay. Walk five verified minutes, light your part of Earth, and pass it on."
                  : "The first Earth Relay has closed. Explore the verified cities, steps, and countries humanity lit together."}
          </p>
          {invite ? (
            <div className="invite-proof" role="status">
              <span><Sparkles size={17} /></span>
              <div>
                <small>YOUR INVITATION IS READY</small>
                <strong>
                  {invite.city}, {invite.countryOrRegion}
                </strong>
              </div>
              <b>{invite.conversions.toLocaleString()} joined</b>
            </div>
          ) : null}
          <div className="hero-actions">
            <a
              className="button button--primary"
              href={primaryActionDestination}
              onClick={campaignPhase === "live" ? trackAppStoreOutbound : undefined}
            >
              <Download size={18} />
              {invite
                ? "CONTINUE ON IPHONE"
                : campaignPhase === "scheduled"
                  ? "APP STORE LAUNCH SOON"
                  : campaignPhase === "live"
                    ? "JOIN FROM IPHONE"
                    : "EXPLORE THE FINAL MAP"}
            </a>
            {useNativeShare ? (
              <button className="button button--ghost" type="button" onClick={() => void share()}>
                {shareState === "copied" ? <Check size={18} /> : <Share2 size={18} />}
                {shareState === "copied"
                  ? "LINK COPIED"
                  : campaignPhase === "live"
                    ? "SHARE THE LIVE MAP"
                    : "SHARE THE WORLD MAP"}
              </button>
            ) : (
              <a
                className="button button--ghost"
                href={xIntentURL(worldShareText, worldShareURL)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Share2 size={18} />
                {campaignPhase === "live"
                  ? "POST LIVE MAP ON X"
                  : "POST WORLD MAP ON X"}
              </a>
            )}
          </div>
          {campaignPhase === "live" ? (
            <div
              className="countdown"
              aria-label={`${campaignClock.label} remaining`}
            >
              <span className="countdown-label">EARTH RELAY CLOSES IN</span>
              <div className="countdown-units">
                <TimeUnit value={campaignClock.hours} label="HRS" />
                <b>:</b>
                <TimeUnit value={campaignClock.minutes} label="MIN" />
                <b>:</b>
                <TimeUnit value={campaignClock.seconds} label="SEC" />
              </div>
            </div>
          ) : (
            <div className="countdown" role="status">
              <span className="countdown-label">
                {campaignPhase === "scheduled" ? "72-HOUR CLOCK" : "EARTH RELAY"}
              </span>
              <div className="countdown-state">
                <strong>
                  {campaignPhase === "scheduled"
                    ? "STARTS AT APP LAUNCH"
                    : "CAMPAIGN COMPLETE"}
                </strong>
                <small>
                  {campaignPhase === "scheduled"
                    ? "Everyone begins together."
                    : "The verified final map stays visible."}
                </small>
              </div>
            </div>
          )}
        </div>

        <div className="hero-visual">
          <div className="hero-image-card">
            <img src="/earth-hero.png" alt="" />
            <div className="image-shade" />
            <div className="hero-stat">
              <span>
                {campaignPhase === "scheduled"
                  ? "VERIFIED WALKERS"
                  : campaignPhase === "live"
                    ? "WALKERS RIGHT NOW"
                    : "FINAL WALKER TOTAL"}
              </span>
              <strong>{formatCompact(world.walkers)}</strong>
              <small>
                <span className="live-dot" />
                {campaignPhase === "scheduled"
                  ? "waiting for the first verified walk"
                  : campaignPhase === "live"
                    ? "verified live total"
                    : "verified final total"}
              </small>
            </div>
          </div>
        </div>
      </section>

      <section className="metric-ribbon" aria-label="Campaign progress">
        <Metric value={world.relayPasses.toLocaleString()} label="RELAYS PASSED" icon={<Share2 />} />
        <Metric value={formatCompact(world.totalSteps)} label={`VERIFIED STEPS · ${formatCompact(world.stepGoal)} GOAL`} icon={<MapPin />} />
        <Metric value={world.regionsReached.toString()} label="COUNTRIES & REGIONS" icon={<Globe2 />} />
        <div className="mission-metric">
          <div>
            <span>
              {campaignPhase === "scheduled"
                ? "EARTHLIGHT"
                : campaignPhase === "live"
                  ? "EARTHLIGHT"
                  : "FINAL EARTHLIGHT"}
            </span>
            <strong>{Math.round(world.missionProgress * 100)}%</strong>
          </div>
          <div className="progress-track" aria-label={`${Math.round(world.missionProgress * 100)} percent`}>
            <span style={{ width: `${world.missionProgress * 100}%` }} />
          </div>
        </div>
      </section>

      <section className="map-section" id="world">
        <div className="section-heading">
          <div>
            <span className="kicker">A PLANET WAKING UP</span>
            <h2>Every city passes the light.</h2>
          </div>
          <p>
            Public markers use approximate cells, never a person’s route.
            The brightness reflects verified community activity.
          </p>
        </div>

        <WorldMap
          cities={cities}
          selectedCityId={selectedCity?.id}
          onSelectCity={setSelectedCity}
        />

        {latestPulse ? (
          <div className="pulse-banner" role="status">
            <span className="pulse-icon"><Sparkles size={17} /></span>
            <div>
              <small>LATEST VERIFIED WALK</small>
              <strong>
                Walker #{latestPulse.contributorNumber.toLocaleString()} lit{" "}
                {latestPulse.city}
              </strong>
            </div>
            <span>{latestPulse.countryOrRegion}</span>
          </div>
        ) : (
          <div className="pulse-banner pulse-banner--empty" role="status">
            <span className="pulse-icon"><Sparkles size={17} /></span>
            <div>
              <small>
                {campaignPhase === "scheduled"
                  ? "THE LAUNCH WORLD IS READY"
                  : campaignPhase === "live"
                    ? "THE FIRST LIGHT IS WAITING"
                    : "THE EARTH RELAY IS COMPLETE"}
              </small>
              <strong>
                {campaignPhase === "scheduled"
                  ? "The first verified walk appears after launch."
                  : campaignPhase === "live"
                    ? "The first verified walk will appear here."
                    : "Explore the final verified city totals."}
              </strong>
            </div>
            <span>
              {campaignPhase === "scheduled"
                ? "72-hour clock waiting"
                : campaignPhase === "live"
                  ? "Be the first light"
                  : "Final map"}
            </span>
          </div>
        )}

        <div className="city-grid">
          {topCities.map((city, index) => (
            <button
              className="city-card"
              key={city.id}
              type="button"
              onClick={() => setSelectedCity(city)}
            >
              <span className="city-rank">{String(index + 1).padStart(2, "0")}</span>
              <div>
                <strong>{city.name}</strong>
                <span>{city.countryOrRegion}</span>
              </div>
              <div className="city-activity">
                <b>{city.activeToday.toLocaleString()}</b>
                <span>today</span>
              </div>
              <ChevronRight size={18} />
            </button>
          ))}
        </div>
      </section>

      <section className="how-section" id="how">
        <div className="how-copy">
          <span className="kicker">ONE SMALL WALK. ONE HUMAN RELAY.</span>
          <h2>Five minutes moves the whole planet.</h2>
          <p>
            Open Earth Relay, walk as usual, and submit. Our server checks the
            movement without publishing your exact route.
          </p>
          <div className="step-list">
            <Step number="01" title="Start a five-minute walk" text="Location runs only during your active session." />
            <Step number="02" title="Add to 10 million steps" text="Verified movement adds light to your city and the shared planet." />
            <Step number="03" title="Pass the relay onward" text="Share your eight-second result and invite the next human." />
          </div>
        </div>
        <aside className="phone-card" id="download">
          <div className="phone-glow" />
          <div className="phone-frame">
            <div className="phone-island" />
            <img src="/earth-hero.png" alt="Earth Relay app world artwork" />
            <span className="phone-kicker">VERIFIED STEP TOTAL</span>
            <strong>{world.totalSteps.toLocaleString()}</strong>
            <p>
              {campaignPhase === "scheduled"
                ? "THE 72-HOUR CLOCK IS WAITING"
                : world.walkers === 0
                  ? "YOUR WALK CAN START THE WORLD"
                  : "STEPS ARE LIGHTING THE WORLD"}
            </p>
            {campaignPhase === "scheduled" ? (
              <div className="phone-button">PREVIEW BEFORE LAUNCH</div>
            ) : (
              <a
                className="phone-button phone-button--link"
                href={APP_STORE_URL}
                onClick={campaignPhase === "live" ? trackAppStoreOutbound : undefined}
              >
                {campaignPhase === "live"
                  ? world.walkers === 0
                    ? "BE THE FIRST LIGHT"
                    : "ADD MY LIGHT"
                  : "VIEW IN THE APP"}
              </a>
            )}
          </div>
          <div className="download-copy">
            <span>
              {campaignPhase === "scheduled"
                ? "COMING TO THE APP STORE"
                : campaignPhase === "live"
                  ? "NOW ON THE APP STORE"
                  : "THE FINAL WORLD REMAINS"}
            </span>
            <strong>Walk five minutes. Pass it around Earth.</strong>
            <p>iPhone · English, Simplified Chinese, and Traditional Chinese</p>
            {campaignPhase === "scheduled" ? (
              <small className="store-status">Available when the shared clock begins.</small>
            ) : (
              <a
                className="store-link"
                href={APP_STORE_URL}
                onClick={campaignPhase === "live" ? trackAppStoreOutbound : undefined}
              >
                OPEN APP STORE <ArrowUpRight size={13} />
              </a>
            )}
          </div>
        </aside>
      </section>

      <section className="privacy-section" id="privacy">
        <div>
          <span className="kicker">DESIGNED FOR DIGNITY</span>
          <h2>The world sees a glow.<br />Not where you went.</h2>
        </div>
        <ul>
          <li><Check /> Location is collected only during an active walk.</li>
          <li><Check /> Public cells are deliberately approximate.</li>
          <li><Check /> Raw route evidence expires after anti-cheat review.</li>
          <li><Check /> First-party funnel events contain no route points and expire after 90 days.</li>
          <li><Check /> Delete your contribution history and linked events in-app.</li>
        </ul>
      </section>

      <footer>
        <a className="brand" href="#"><span className="brand-mark"><Globe2 size={21} /></span>EARTH RELAY</a>
        <p>A global cooperative experience by TalkApe (Hangzhou) Technology Co., Ltd.</p>
        <div>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/support">Support</a>
          <a href="/accessibility">Accessibility</a>
        </div>
      </footer>

      {selectedCity ? (
        <div className="city-sheet" role="dialog" aria-modal="true" aria-label={`${selectedCity.name} details`}>
          <button type="button" onClick={() => setSelectedCity(undefined)} aria-label="Close city details"><X /></button>
          <span className="kicker">CITY PULSE · LEVEL {selectedCity.level}</span>
          <h3>{selectedCity.name}</h3>
          <p>{selectedCity.countryOrRegion}</p>
          <div className="sheet-stats">
            <div><strong>{selectedCity.contributors.toLocaleString()}</strong><span>contributors</span></div>
            <div><strong>{selectedCity.activeToday.toLocaleString()}</strong><span>today</span></div>
          </div>
          <div className="progress-track"><span style={{ width: `${selectedCity.progress * 100}%` }} /></div>
          <small>{Math.round(selectedCity.progress * 100)}% to the next light level</small>
        </div>
      ) : null}
    </main>
  );
}

function legalKindForPath(
  pathname: string
): "privacy" | "terms" | "support" | "accessibility" | undefined {
  const segment = pathname.replace(/^\/+|\/+$/g, "");
  if (
    segment === "privacy"
    || segment === "terms"
    || segment === "support"
    || segment === "accessibility"
  ) {
    return segment;
  }
  return undefined;
}

export function inviteCodeFromSearch(search: string): string | undefined {
  const code = new URLSearchParams(search).get("invite") ?? undefined;
  return code && /^[A-Za-z0-9_-]{10,32}$/.test(code) ? code : undefined;
}

function Metric({ value, label, icon }: { value: string; label: string; icon: React.ReactNode }) {
  return (
    <div className="metric">
      <span className="metric-icon">{icon}</span>
      <div><strong>{value}</strong><span>{label}</span></div>
    </div>
  );
}

function TimeUnit({ value, label }: { value: number; label: string }) {
  return <span><strong>{String(value).padStart(2, "0")}</strong><small>{label}</small></span>;
}

function Step({ number, title, text }: { number: string; title: string; text: string }) {
  return (
    <div className="step">
      <span>{number}</span>
      <div><strong>{title}</strong><p>{text}</p></div>
    </div>
  );
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(value);
}

export type CampaignPhase = "scheduled" | "live" | "ended";

export function appStoreDestinationForPhase(phase: CampaignPhase): string {
  return phase === "scheduled" ? "#download" : APP_STORE_URL;
}

export function socialShareURL(
  currentURL: string,
  phase: CampaignPhase,
  totalSteps: number,
  now: number = Date.now()
): string {
  const url = new URL(currentURL);
  url.hash = "";
  if (phase === "scheduled") {
    url.searchParams.delete("snapshot");
  } else {
    const revision = phase === "live"
      ? `${phase}-${Math.max(0, Math.floor(totalSteps))}-${Math.floor(now / 60_000)}`
      : `${phase}-${Math.max(0, Math.floor(totalSteps))}`;
    url.searchParams.set("snapshot", revision);
  }
  return url.toString();
}

export function xIntentURL(text: string, url: string): string {
  const intent = new URL("https://twitter.com/intent/tweet");
  intent.searchParams.set("text", text);
  intent.searchParams.set("url", url);
  return intent.toString();
}

export function campaignPhaseAt(
  startsAt: string,
  endsAt: string,
  now: number = Date.now()
): CampaignPhase {
  const start = Date.parse(startsAt);
  const end = Date.parse(endsAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return "scheduled";
  }
  if (now < start) return "scheduled";
  if (now < end) return "live";
  return "ended";
}

function useCampaignClock(startsAt: string, endsAt: string) {
  const [, redraw] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => redraw((value) => value + 1), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const now = Date.now();
  const phase = campaignPhaseAt(startsAt, endsAt, now);
  const milliseconds = phase === "live"
    ? Math.max(0, Date.parse(endsAt) - now)
    : 0;
  const totalSeconds = Math.floor(milliseconds / 1_000);
  return {
    phase,
    hours: Math.floor(totalSeconds / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
    label: `${Math.floor(totalSeconds / 3_600)} hours`
  };
}
